/* eslint-disable require-await */
/* eslint-disable unicorn/no-static-only-class */

import $ from 'jquery';
// import { cssAnimation } from '../../util/animate-css.js';

/**
 * @class MpWidgetError
 */
export class MpWidgetError extends Error {

	static NAME = 'MpWidgetError';

	/**
	 * @param {{ status: any; error: any; message: any; }} obj
	 */
	constructor(obj) {
		super(obj.message);
		this.status = obj.status;
		this.error = obj.error;
		// eslint-disable-next-line unicorn/custom-error-definition
		this.name = MpWidgetError.NAME;
	}
}

/**
 * @abstract
 * @class MpBaseWidget
 */
export default class MpBaseWidget {

	/** @type {string} */
	static NAME = 'MpBaseWidget';

	/**
	 * @returns {DynamicWidgetConfig}
	 */
	static get config() {
		throw new MpWidgetError({
			status: 500,
			error: 'NotImplemented',
			message: `${this.NAME}.config getter not implemented. You must implement this getter in your plugin.`,
		});
	}

	static getEventNs() {
		return `plugin:${this.config.name}`;
	}

	static getDataName() {
		return `plugin:${this.config.name}`;
	}

	/**
	 * @param { JQuery | HTMLElement } element
	 */
	static getOptions(element) {
		/** @type {JQuery} */ // @ts-ignore
		const $element = element.jquery ? element : $(element);
		return { ...$element.data(this.config.optionsDataName) };
	}

	/**
	 * @param {HTMLElement | JQuery} element
	 *
	 * @returns {Object | Promise<Object>}
	 */
	static getInstanceRef(element) {
		// @ts-ignore
		return (element.jquery ? element : $(element)).data(this.getDataName());
	}

	/**
	 * @param {HTMLElement | JQuery} element
	 */
	static getOrCreateInstance(element) {

		const $element = $(element);
		const instance = this.getInstanceRef($element);

		if (instance) {
			return instance;
		}

		return this.create($element);
	}

	/**
	 * @param {any} instance
	 */
	static isInstanceLoaded(instance) {
		// return instance instanceof Promise ? false : true;
		return instance ? (instance.then ? false : true) : false;
	}

	/**
	 * @protected
	 *
	 * @param {JQuery} $element
	 */
	static addCustomHandlersBefore($element) {
		$element.on(`disable.bind-change-action.${this.getEventNs()}`, async (e, { disabled }) => {
			// Input is re-enabled, so we need to re-initialize the plugin
			if (disabled === false) {
				await this.destroy($element);
				await this.create($element);
			}
		});
	}

	/**
	 * @protected
	 *
	 * @param {JQuery} $element
	 */
	static removeCustomHandlersBefore($element) {
		$element.off(`disable.bind-change-action.${this.getEventNs()}`);
	}

	/**
	 * Enhance all elements matching the selector with the plugin
	 *
	 * @param {JQuery} $elements
	 */
	static enhanceElements($elements) {
		for (const element of $elements) {
			this.init(element);
		}
	}

	/**
	 * @param {HTMLElement | JQuery} element
	 */
	static async init(element) {

		const { config } = this;
		/** @type {JQuery} */ // @ts-ignore
		const $element = element.jquery ? element : $(element);
		const currentInstance = $element.data(this.getDataName());

		// Check if plugin is already initialized
		if (currentInstance) {
			if (config.allowReInit === false) {
				return currentInstance;
			}
			await this.destroy($element);
		}

		// ----------------------
		// Initialize the plugin
		// ----------------------
		return this.create($element);
	}

	/**
	 * @protected
	 *
	 * @param {JQuery} $element
	 */
	static async create($element) {

		// Handle custom events before initialization
		this.addCustomHandlersBefore($element);

		// ----------------------
		// Initialize the plugin
		// ----------------------

		// Make sure the loader is added to a div element
		const $loaderContext = $element.is('input,textarea,select') ? $element.closest('div') : $element;
		const height = $loaderContext.height();
		const loaderHeight = (height > 300)
			? ''
			: (
				(height > 100)
					? 'md'
					: (
						(height > 32)
							? 'sm'
							: 'xs'
					)
			);
		mpro.addContentLoader($loaderContext, [loaderHeight], { delay: 150 });

		try {
			// Init the instance (Promise)
			const instance = this.createInstance($element, this.getOptions($element));

			$element
				// Assign the promise with the instance reference to the element
				.data(this.getDataName(), instance)
				// Add destroy event
				.on(`destroy.${this.getEventNs()}`, () => {
					this.destroy($element);
				});

			return await instance.then((instance) => {
				// Replace the promise with the instance reference
				$element.data(this.getDataName(), instance);
				// cssAnimation($loaderContext[0], 'fadeIn', 'fastest');
				return instance;
			});
		}
		catch (error) {
			console.error('`%s` plugin createInstance error: %o', this.NAME, error);
			return null;
		}
		finally {
			mpro.removeContentLoader($loaderContext);
		}
	}

	/**
	 * @protected
	 *
	 * @param {JQuery} $element
	 * @param {any} options
	 *
	 * @returns {Promise<any>}
	 */
	static async createInstance($element, options) {
		// Import the createInstance function dynamically to avoid loading the plugin when not needed
		const { createInstance } = await import(`./widgets/${this.config.name}/create-instance.js`);
		return await createInstance(this, $element, options);
	}

	/**
	 * @param {HTMLElement | JQuery} element
	 */
	static async destroy(element) {

		/** @type {JQuery} */ // @ts-ignore
		const $element = element.jquery ? element : $(element);
		const instance = this.getInstanceRef($element);

		if (!instance) {
			return;
		}

		// Remove custom event handlers
		this.removeCustomHandlersBefore($element);

		// Destroy the JQuery references first
		$element.off(`.${this.getEventNs()}`).removeData(this.getDataName());

		// Destroy the instance
		await this.destroyInstance(instance, $element);
	}

	/**
	 * @protected
	 *
	 * @param {any} instance
	 * @param {JQuery} $element
	 * @returns {Promise<any>}
	 */
	static async destroyInstance(instance, $element) {

		// Import the destroyInstance function dynamically to avoid loading the plugin when not needed
		const [inst, { cleanupElement, destroyInstance }] = await Promise.all([
			instance,
			import(`./widgets/${this.config.name}/create-instance.js`),
		]);
		destroyInstance(this, inst);
		cleanupElement(this, $element);
	}
}
